import { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Badge, Button } from '@mui/material';
import { useSelector } from 'react-redux';
import { isEmpty } from 'lodash';
import usePrintLabelContext from 'common/hooks/usePrintLabelContext';
import BulkManageTagsDialog from './BulkManageTagsDialog';

const BulkTagItemButton = ({ onSuccess }) => {
  const { labelItemIds } = usePrintLabelContext();
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const { pages, currentPage } = useSelector(({ workQueue }) => workQueue.bulkLabels);

  const bulkLabels = useMemo(() => pages[currentPage] || [], [pages, currentPage]);

  /**
   * POM-6061: the rows on the current page that are selected — which is what the badge counts as
   * well as what the dialog acts on.
   *
   * `labelItemIds` can outlive the rows it was picked from (a list response landing out of order,
   * a refresh replacing page 1), and counting the badge off it advertised a selection the dialog
   * then opened empty, with no checkbox ticked anywhere on screen. Derived rather than held in
   * state: the effect that used to set it left the count one render behind the selection.
   */
  const selectedLineItems = useMemo(
    () =>
      isEmpty(labelItemIds) || isEmpty(bulkLabels)
        ? []
        : bulkLabels.filter((item) => labelItemIds.includes(item.lineItemId)),
    [labelItemIds, bulkLabels]
  );

  const handleOpenDialog = () => setIsDialogOpen(true);

  const handleCloseDialog = () => setIsDialogOpen(false);

  const handleSuccess = () => {
    handleCloseDialog();
    if (onSuccess) {
      onSuccess();
    }
  };

  const itemCount = selectedLineItems.length;
  const isDisabled = itemCount === 0;

  return (
    <>
      <Badge color='error' badgeContent={itemCount} sx={{ mx: 1 }}>
        <Button
          variant='contained'
          color='primary'
          onClick={handleOpenDialog}
          disabled={isDisabled}
          sx={{ height: 33 }}
        >
          Tag Items
        </Button>
      </Badge>

      {isDialogOpen && (
        <BulkManageTagsDialog
          open={isDialogOpen}
          onClose={handleCloseDialog}
          selectedLineItems={selectedLineItems}
          onSuccess={handleSuccess}
        />
      )}
    </>
  );
};

BulkTagItemButton.propTypes = {
  onSuccess: PropTypes.func,
};

BulkTagItemButton.defaultProps = {
  onSuccess: undefined,
};

export default BulkTagItemButton;
